import { useEffect, useState } from "react";
import { StatusBadge } from "@/components/Bits";
import api, { COUNTRY_NAMES, fmt, fmtTime } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ContactHistory = ({ contact, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!contact) { setData(null); return; }
    api.get(`/contacts/${contact.id}/history`).then((r) => setData(r.data));
  }, [contact]);

  return (
    <Dialog open={!!contact} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="contact-history-dialog">
        <DialogHeader>
          <DialogTitle>
            Historique · <span className="mono">{contact?.phone}</span>
          </DialogTitle>
        </DialogHeader>

        {!data ? <p className="text-sm text-gray-500">Chargement…</p> : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
              <span>{data.contact.first_name || "—"}</span>
              <span>{COUNTRY_NAMES[data.contact.country] || data.contact.country}</span>
              <StatusBadge status={data.contact.status} />
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="history-stats">
              {[["Campagnes", data.stats.campaigns], ["Envoyés", data.stats.sent],
                ["Livrés", data.stats.delivered], ["Lus", data.stats.read],
                ["Échecs", data.stats.failed], ["Réponses", data.stats.replies]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-gray-200 p-2.5">
                  <p className="text-[11px] text-gray-500">{l}</p>
                  <p className="mono text-lg font-semibold text-gray-900">{fmt(v)}</p>
                </div>
              ))}
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Campagnes reçues</p>
              <ul className="space-y-2" data-testid="history-sends">
                {data.sends.length === 0 && <li className="text-sm text-gray-500">Aucun envoi.</li>}
                {data.sends.map((s) => (
                  <li key={s.queue_id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{s.campaign_name}</p>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{s.message_text}</p>
                        <p className="mono mt-1 text-[11px] text-gray-400">
                          {s.sent_at ? fmtTime(s.sent_at) : `prévu ${fmtTime(s.scheduled_at)}`} · {s.instance_label}
                          {s.delivery_status ? ` · ${s.delivery_status}` : ""}
                        </p>
                        {s.error_detail && <p className="text-[11px] text-red-600 mt-1">{s.error_detail}</p>}
                      </div>
                      <StatusBadge status={s.status} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Réponses et messages</p>
              <ul className="space-y-2" data-testid="history-replies">
                {data.replies.length === 0 && <li className="text-sm text-gray-500">Aucune réponse.</li>}
                {data.replies.map((r) => (
                  <li key={r.id} className={`rounded-lg p-2.5 ${r.direction === "out" ? "bg-[#E7F7EF]" : "bg-gray-100"}`}>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{r.text}</p>
                    <p className="mono mt-1 text-[10px] text-gray-500">
                      {r.direction === "out" ? (r.ai_generated ? "IA" : "Vous") : "Client"} · {fmtTime(r.at)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
